/**
 * Hook for URL validation with consistent behavior across the application
 * Provides both strict validation (with error throwing) and simple validation (boolean)
 */
const useUrlValidation = () => {
  /**
   * Validates a URL and throws an error if invalid
   * @param {string} inputUrl - The URL to validate
   * @returns {string} The validated URL (without modifying the protocol)
   * @throws {Error} If the URL is invalid
   */
  const validateUrl = (inputUrl) => {
    const trimmedUrl = inputUrl.trim();

    if (!trimmedUrl) {
      throw new Error('Please enter a URL to create a QR code');
    }

    // Check if it's a valid URL with or without protocol
    // More restrictive pattern for better security
    const urlPattern =
      /^((https?:\/\/)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?::\d{1,5})?(?:\/[-\w@:%_+.~#?&//=]*)?)$/i;

    if (!urlPattern.test(trimmedUrl)) {
      throw new Error(
        'Please enter a valid URL (e.g., example.com or https://example.com)',
      );
    }

    // Additional security checks
    if (trimmedUrl.length > 2048) {
      throw new Error('URL is too long');
    }

    // Check for potentially malicious patterns
    const maliciousPatterns = [
      /<script[^>]*>/i,
      /javascript:/i,
      /data:/i,
      /vbscript:/i,
      /\b(?:on\w+|xmlns\b)/i,
    ];

    if (maliciousPatterns.some((pattern) => pattern.test(trimmedUrl))) {
      throw new Error('Invalid URL pattern detected');
    }

    // Additional validation for domain format
    try {
      // Try to create URL object with a temporary protocol if needed
      const testUrl = trimmedUrl.includes('://')
        ? trimmedUrl
        : `https://${trimmedUrl}`;
      new URL(testUrl);
      return trimmedUrl; // Return the original URL without modification
    } catch (_) {
      throw new Error('Please enter a properly formatted URL');
    }
  };

  /**
   * Simple URL validation without throwing errors
   * @param {string} url - The URL to validate
   * @returns {boolean} True if the URL is valid
   */
  const isValidUrl = (url) => {
    if (!url) return false;

    try {
      // Test with a temporary protocol if needed
      const testUrl = url.includes('://') ? url : `https://${url}`;
      new URL(testUrl);
      return true;
    } catch (_) {
      return false;
    }
  };

  /**
   * Checks if a character is safe for URLs
   * @param {string} char - Single character to check
   * @returns {boolean} True if the character is safe
   */
  const isSafeChar = (char) => {
    const charCode = char.charCodeAt(0);
    // Allow alphanumeric and common URL-safe characters
    return (
      (charCode >= 48 && charCode <= 57) || // 0-9
      (charCode >= 65 && charCode <= 90) || // A-Z
      (charCode >= 97 && charCode <= 122) || // a-z
      "-._~:/?#[]@!$&'()*+,;=".includes(char) // URL-safe special chars
    );
  };

  /**
   * Sanitizes a URL while preserving its structure
   * @param {string} url - The URL to sanitize
   * @returns {string} The sanitized URL
   */
  const normalizeUrl = (url) => {
    if (!url) return '';

    // Convert to string and trim
    let normalized = String(url).trim();

    // Remove any HTML/script tags
    normalized = normalized.replace(/<[^>]*>?/gm, '');

    // Filter out unsafe characters using our safe character check
    normalized = normalized.split('').filter(isSafeChar).join('');

    // Additional security: Remove any remaining suspicious patterns
    const suspiciousPatterns = [
      /\s*(javascript|data|vbscript):/gi,
      /\s+on\w+\s*=\s*["'][^"']*["']/gi,
    ];

    suspiciousPatterns.forEach((pattern) => {
      normalized = normalized.replace(pattern, '');
    });

    return normalized;
  };

  return {
    validateUrl,
    isValidUrl,
    normalizeUrl,
  };
};

export default useUrlValidation;
